import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Check, Home, Building, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useAddress, type Address } from '@/context/AddressContext';
import { authFetch } from '@/services/api';
import { MetroProgress } from '@/components/checkout/MetroProgress';
import { calculatePriceSummary, type ShippingConfig } from '@/utils/priceUtils';
import DeliveryTimeline from '@/components/DeliveryTimeline';
import { useToast } from '@/hooks/use-toast';

const ReviewOrderPage = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const { user, setShowSignInModal } = useAuth();
  const { addresses, defaultAddress, selectedAddress, setSelectedAddress, setDefaultAddress } = useAddress();
  const { toast } = useToast();
  
  const [isAddressExpanded, setIsAddressExpanded] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [shippingConfig, setShippingConfig] = useState<ShippingConfig>({
    enabled: false,
    freeThreshold: 0,
    flatCharge: 0,
    gstRate: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(true);

  // Fetch shipping config from server (like mobile)
  useEffect(() => {
    let mounted = true;
    
    const fetchShippingConfig = async () => {
      try {
        const resp = await authFetch('/api/config/shipping');
        if (!resp.ok) return;
        
        const cfg = await resp.json();
        if (!mounted) return;
        
        setShippingConfig({
          enabled: !!cfg.enabled,
          freeThreshold: Number(cfg.freeThreshold || 0),
          flatCharge: Number(cfg.flatCharge || 0),
          gstRate: Number(cfg.gstRate || 0),
        });
      } catch (e) {
        console.warn('Failed to fetch shipping config', e);
      }
    };
    
    fetchShippingConfig();
    return () => { mounted = false; };
  }, []);

  // Calculate price summary using server config (like mobile)
  const priceSummary = useMemo(() => {
    return calculatePriceSummary({
      items: cart,
      shippingConfig,
    });
  }, [cart, shippingConfig]);

  // Initialize selected address with default address if none selected
  useEffect(() => {
    if (!selectedAddress && defaultAddress) {
      setSelectedAddress(defaultAddress);
    }
  }, [selectedAddress, defaultAddress, setSelectedAddress]);

  useEffect(() => {
    setShippingConfig({
      enabled: true,
      freeThreshold: 999,
      flatCharge: 99,
      gstRate: 18
    });
  }, []);

  // Handle address selection
  const handleSelectAddress = async (address: Address) => {
    try {
      await setDefaultAddress(address.addressId);
      setSelectedAddress(address);
      setIsAddressExpanded(false);
      toast({
        title: 'Address Updated',
        description: 'Delivery address has been updated.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update address.',
        variant: 'destructive',
      });
    }
  };

  // Handle adding new address
  const handleAddAddress = () => {
    setShowAddressModal(true);
  };

  // Get address type icon
  const getAddressTypeIcon = (type: string) => {
    switch (type) {
      case 'HOME': return Home;
      case 'WORK': return Building;
      case 'OTHER': default: return User;
    }
  };

  // Get address type color
  const getAddressTypeColor = (type: string) => {
    switch (type) {
      case 'HOME': return 'text-green-600';
      case 'WORK': return 'text-blue-600';
      case 'OTHER': default: return 'text-gray-600';
    }
  };

  const handleProceedToPayment = async () => {
    console.log('Proceed to payment clicked. User state:', {
      isAuthenticated: user.isAuthenticated,
      customerId: user.customerId
    });
    
    if (!user.isAuthenticated) {
      console.log('User not authenticated, showing sign in modal');
      setShowSignInModal(true);
      return;
    }

    if (!selectedAddress) {
      alert('Please select a delivery address');
      return;
    }

    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }

    setIsLoading(true);

    try {
      const orderData = {
        totalAmount: priceSummary.grandTotal,
        shippingCharge: priceSummary.shippingCharge,
        shippingAddress: `${selectedAddress.fullName}\n${selectedAddress.addressLine1}${selectedAddress.addressLine2 ? ', ' + selectedAddress.addressLine2 : ''}, ${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.postalCode}`,
        shippingCity: selectedAddress.city,
        shippingState: selectedAddress.state,
        shippingPincode: selectedAddress.postalCode,
        phoneNumber: selectedAddress.phoneNumber,
        items: cart.map((item) => ({
          variantId: item.variantId,
          quantity: item.quantity,
          price: item.salePrice || item.offerPrice || 0
        }))
      };

      const response = await authFetch('/api/orders/place', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Server error' }));
        throw new Error(errorData.error || 'Order failed');
      }

      const parsedData = await response.json();

      navigate('/payment', {
        state: {
          paymentData: parsedData,
          customerDetails: {
            shippingAddress: orderData.shippingAddress,
            phoneNumber: selectedAddress.phoneNumber,
            customerId: user.customerId
          }
        }
      });
    } catch (error: any) {
      alert(error.message || 'Failed to place order');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user.isAuthenticated) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Sign In Required</h1>
          <p className="text-muted-foreground mb-6">Please sign in to review your order.</p>
          <Button onClick={() => setShowSignInModal(true)}>Sign In</Button>
        </div>
      </Layout>
    );
  }

  if (cart.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Your cart is empty</h1>
          <p className="text-muted-foreground mb-6">Add some products to get started!</p>
          <Button onClick={() => navigate('/')}>Continue Shopping</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <MetroProgress steps={['Cart', 'Review', 'Payment']} currentStep={1} />
      
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">Review Your Order</h1>
          
          {/* Address Section */}
          <div className="bg-card rounded-lg p-6 mb-6 border">
            <div className="flex items-start gap-3 mb-4">
              <MapPin className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold mb-2">Delivery Address</h3>
                {selectedAddress ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{selectedAddress.fullName}</p>
                        <p className="text-sm text-muted-foreground">
                          {selectedAddress.addressLine1}
                          {selectedAddress.addressLine2 && `, ${selectedAddress.addressLine2}`}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.postalCode}
                        </p>
                        <p className="text-sm text-muted-foreground">{selectedAddress.phoneNumber}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setIsAddressExpanded(!isAddressExpanded)}
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <MapPin className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground mb-4">No Saved Addresses</p>
                    <p className="text-sm text-muted-foreground mb-4">Add a delivery address to continue</p>
                    <Button variant="outline" onClick={handleAddAddress}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Address
                    </Button>
                  </div>
                )}

                {/* Address Selection Modal */}
                <AnimatePresence>
                  {isAddressExpanded && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="mt-4 p-4 bg-secondary/50 rounded-lg border"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">Select Delivery Address</h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setIsAddressExpanded(false)}
                        >
                          Cancel
                        </Button>
                      </div>

                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {addresses.map((address) => {
                          const AddressIcon = getAddressTypeIcon(address.addressType);
                          const iconColor = getAddressTypeColor(address.addressType);

                          return (
                            <div
                              key={address.addressId}
                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                selectedAddress?.addressId === address.addressId
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50 bg-background'
                              }`}
                              onClick={() => handleSelectAddress(address)}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full bg-secondary flex items-center justify-center ${iconColor}`}>
                                  <AddressIcon className="h-4 w-4" />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-sm">{address.addressType}</span>
                                    {address.isDefault && (
                                      <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                                        Default
                                      </span>
                                    )}
                                  </div>
                                  <p className="font-medium text-sm">{address.fullName}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {address.addressLine1}
                                    {address.addressLine2 && `, ${address.addressLine2}`}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {address.city}, {address.state} - {address.postalCode}
                                  </p>
                                </div>
                                {selectedAddress?.addressId === address.addressId && (
                                  <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* Add New Address Option */}
                        <div
                          className="p-3 rounded-lg border-2 border-dashed border-primary cursor-pointer hover:bg-primary/5 transition-colors"
                          onClick={handleAddAddress}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <Plus className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium text-primary">Add New Address</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-card rounded-lg p-6 mb-6 border">
            <h3 className="font-semibold mb-4">Order Summary ({cart.length} items)</h3>
            <div className="space-y-4">
              {cart.map((item) => {
                const image = item.images?.[0]?.imageUrl || '/placeholder-product.png';
                const hasDiscount = item.originalPrice && item.originalPrice > item.salePrice;

                return (
                  <div key={item.variantId} className="flex gap-4 pb-4 border-b last:border-b-0">
                    <img
                      src={image}
                      alt={item.productName}
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium mb-1">{item.productName}</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Size: {item.size} • Color: {item.color}
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold">₹{item.salePrice}</span>
                        {hasDiscount && (
                          <span className="text-sm text-muted-foreground line-through">
                            ₹{item.originalPrice}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">Qty: {item.quantity}</p>
                      
                      {/* Delivery Timeline */}
                      <DeliveryTimeline
                        items={[{ seller: item.seller }]}
                        buyerAddress={selectedAddress ? {
                          city: selectedAddress.city,
                          state: selectedAddress.state
                        } : null}
                        shipping={{
                          isFree: priceSummary.shippingCharge === 0,
                          cost: priceSummary.shippingCharge
                        }}
                        className="mt-2"
                      />
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">₹{item.salePrice * item.quantity}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Price Details */}
          <div className="bg-card rounded-lg p-6 mb-6 border">
            <h3 className="font-semibold mb-4">Price Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span>Total MRP</span>
                <span>₹{priceSummary.totalMrp}</span>
              </div>
              
              {priceSummary.totalDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-₹{priceSummary.totalDiscount}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>
                  {priceSummary.shippingCharge > 0 
                    ? `₹${priceSummary.shippingCharge}` 
                    : 'FREE'
                  }
                </span>
              </div>
              
              <div className="border-t pt-3 flex justify-between font-semibold text-lg">
                <span>You Pay</span>
                <span className="text-primary">₹{priceSummary.grandTotal}</span>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="sticky bottom-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t p-4 mt-6">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div>
                <p className="font-semibold text-lg">₹{priceSummary.grandTotal}</p>
                <p className="text-sm text-muted-foreground">Order Total</p>
              </div>
              <Button 
                size="lg" 
                onClick={handleProceedToPayment}
                disabled={!selectedAddress || isLoading}
                className="min-w-[200px]"
              >
                {isLoading ? 'Processing...' : `Proceed to Pay ₹${priceSummary.grandTotal}`}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Address Modal */}
      <AnimatePresence>
        {showAddressModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddressModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-background rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">Choose an Option</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddressModal(false)}
                >
                  ✕
                </Button>
              </div>
              
              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    setShowAddressModal(false);
                    navigate('/addresses?selectMode=true');
                  }}
                >
                  <MapPin className="h-4 w-4 mr-3" />
                  Select from Saved Addresses
                </Button>
                
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    setShowAddressModal(false);
                    navigate('/address/add');
                  }}
                >
                  <Plus className="h-4 w-4 mr-3" />
                  Add New Address
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default ReviewOrderPage;